import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Header from "./Components/Header";
import { ChevronLeft, ChevronRight, Calendar, Clock } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import PageContainer from "./Components/PageContainer";
import MainContent from "./Components/MainContent";
import BookingSummary from "./Components/BookingSummary";
import PaymentModal from "./Components/PaymentModal";
import {
  getTeacherEmail,
  normalizeAvailabilityCollection,
  teacherApi,
} from "../../features/teacher/teacherApi";
import { getCanonicalYearId } from "../../utils/basicPlanAccess";
import { bookingService } from "../../services/booking.service";
import {
  clearStoredBookingReturnNotice,
  clearStoredBookingStripeReturnGuard,
  persistBookingSummary,
  persistBookingReturnNotice,
  persistBookingStripeReturnGuard,
  readStoredBookingStripeReturnGuard,
  readStoredBookingReturnNotice,
  readStoredBookingSummary,
} from "./bookingFlowStorage";
import {
  normalizeAudPriceForDisplay,
  normalizeAudPriceToMinorUnits,
} from "../../utils/audCurrency";
import {
  getClassBookingActionErrorMessage,
  getClassBookingQueryErrorMessage,
  matchesTechnicalError,
} from "../../utils/uiErrorMessages";
import { isPendingBooking } from "../../utils/bookingStatus";
import {
  getBookingSourceFromPath,
  getClassBookingBasePath,
} from "../../features/parentFlow/parentFlow";
import { logTutorConfirmPayClicked } from "../../utils/datadogLogger";

const AUSTRALIA_TIME_ZONE = "Australia/Sydney";
const PAYMENT_UI_STATES = {
  IDLE: "idle",
  CREATING_CHECKOUT: "creating_checkout",
  REDIRECTING: "redirecting",
  CONFIRMING_PAYMENT: "confirming_payment",
  PAYMENT_CONFIRMED: "payment_confirmed",
  PAYMENT_FAILED_OR_CANCELLED: "payment_failed_or_cancelled",
};
const PAYMENT_CANCELLED_MESSAGE =
  "Payment was cancelled. You can choose the slot again or continue when ready.";

const getAustraliaDateParts = (date) => {
  const parts = new Intl.DateTimeFormat("en-AU", {
    timeZone: AUSTRALIA_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const mappedParts = parts.reduce((result, part) => {
    if (part.type !== "literal") {
      result[part.type] = part.value;
    }
    return result;
  }, {});

  return {
    year: Number(mappedParts.year),
    month: Number(mappedParts.month),
    day: Number(mappedParts.day),
  };
};

const formatMonthLabel = (monthDate) =>
  monthDate.toLocaleDateString("en-AU", {
    timeZone: AUSTRALIA_TIME_ZONE,
    month: "long",
    year: "numeric",
  });

const WEEKDAY_INDEX_BY_LABEL = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

const formatDateKey = (date) => {
  const { year, month, day } = getAustraliaDateParts(date);
  const normalizedMonth = `${month}`.padStart(2, "0");
  const normalizedDay = `${day}`.padStart(2, "0");

  return `${year}-${normalizedMonth}-${normalizedDay}`;
};

const formatAustralianTime = (date) =>
  date.toLocaleTimeString("en-AU", {
    timeZone: AUSTRALIA_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

const formatAustralianDateLabel = (date) =>
  date.toLocaleDateString("en-AU", {
    timeZone: AUSTRALIA_TIME_ZONE,
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const getAustraliaMonthFirstWeekday = (year, month) => {
  const firstDayDate = new Date(Date.UTC(year, month - 1, 1, 12, 0, 0));
  const weekdayLabel = new Intl.DateTimeFormat("en-US", {
    timeZone: AUSTRALIA_TIME_ZONE,
    weekday: "short",
  }).format(firstDayDate);

  return WEEKDAY_INDEX_BY_LABEL[weekdayLabel] ?? 0;
};

const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();

const getCalendarCells = (year, month) => {
  const firstWeekday = getAustraliaMonthFirstWeekday(year, month);
  const totalDays = getDaysInMonth(year, month);

  return [
    ...Array.from({ length: firstWeekday }, (_, index) => ({
      type: "blank",
      id: `blank-${index}`,
    })),
    ...Array.from({ length: totalDays }, (_, index) => ({
      type: "day",
      value: index + 1,
    })),
  ];
};

const parseSlotDate = (value) => {
  if (!value) return null;
  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const isFutureAvailabilitySlot = (slot, now = new Date()) => {
  const startDate = parseSlotDate(slot?.start_time || slot?.startTime);
  if (!startDate) return false;
  return startDate.getTime() > now.getTime();
};

const hasBookableDateSlots = (dateItem) =>
  Array.isArray(dateItem?.slots) &&
  dateItem.slots.some((slot) => !slot?.isDisabled);

const formatSlotTimeRange = (slot) => {
  const startDate = parseSlotDate(slot?.start_time || slot?.startTime);
  const endDate = parseSlotDate(slot?.end_time || slot?.endTime);

  if (!startDate || !endDate) return "Time unavailable";

  const startLabel = formatAustralianTime(startDate);
  const endLabel = formatAustralianTime(endDate);

  return `${startLabel} - ${endLabel}`;
};

const getSlotDurationLabel = (slot) => {
  const startDate = parseSlotDate(slot?.start_time || slot?.startTime);
  const endDate = parseSlotDate(slot?.end_time || slot?.endTime);

  if (!startDate || !endDate) return "60 minutes";

  const durationInMinutes = Math.max(
    0,
    Math.round((endDate.getTime() - startDate.getTime()) / 60000)
  );

  return `${durationInMinutes || 60} minutes`;
};

const buildAvailabilityModel = (slots, now = new Date()) => {
  const groupedSlots = new Map();

  slots.forEach((slot) => {
    const startDate = parseSlotDate(slot?.start_time || slot?.startTime);
    if (!startDate) return;

    const dateKey = formatDateKey(startDate);
    const { year, month, day } = getAustraliaDateParts(startDate);
    const monthKey = `${year}-${month}`;
    const monthDate = new Date(year, month - 1, 1);
    const slotEntry = {
      id: slot?.slot_id || slot?.id || `${dateKey}-${slot?.start_time || slot?.startTime}`,
      label: formatSlotTimeRange(slot),
      dateKey,
      startDate,
      isDisabled: !isFutureAvailabilitySlot(slot, now),
      raw: slot,
    };

    if (!groupedSlots.has(dateKey)) {
      groupedSlots.set(dateKey, {
        dateKey,
        date: startDate,
        dayNumber: day,
        monthKey,
        monthDate,
        displayDate: formatAustralianDateLabel(startDate),
        slots: [],
      });
    }

    groupedSlots.get(dateKey).slots.push(slotEntry);
  });

  const dates = Array.from(groupedSlots.values())
    .map((item) => ({
      ...item,
      slots: [...item.slots].sort(
        (left, right) => left.startDate.getTime() - right.startDate.getTime()
      ),
    }))
    .sort((left, right) => left.date.getTime() - right.date.getTime());

  const months = Array.from(
    dates.filter(hasBookableDateSlots).reduce((monthMap, item) => {
      if (!monthMap.has(item.monthKey)) {
        monthMap.set(item.monthKey, {
          key: item.monthKey,
          date: item.monthDate,
          year: getAustraliaDateParts(item.monthDate).year,
          month: getAustraliaDateParts(item.monthDate).month,
          label: formatMonthLabel(item.monthDate),
        });
      }
      return monthMap;
    }, new Map()).values()
  );

  return {
    dates,
    months,
    hasBookableSlots: dates.some(hasBookableDateSlots),
  };
};

const mergePendingSlotIntoAvailability = ({
  availabilitySlots = [],
  pendingBooking = null,
  fallbackSelectedSlot = null,
}) => {
  const baseSlots = Array.isArray(availabilitySlots) ? availabilitySlots : [];
  if (!pendingBooking || isBookingPaidOrConfirmed(pendingBooking)) {
    return baseSlots;
  }

  const pendingSlot =
    pendingBooking?.slot ||
    pendingBooking?.booking?.slot ||
    fallbackSelectedSlot ||
    null;

  if (!pendingSlot || typeof pendingSlot !== "object") {
    return baseSlots;
  }

  const pendingSlotId = String(
    pendingSlot?.slot_id || pendingSlot?.id || ""
  ).trim();
  const pendingStartTime = String(
    pendingSlot?.start_time || pendingSlot?.startTime || ""
  ).trim();
  const pendingBookingId = String(
    pendingBooking?.booking_id ||
      pendingBooking?.bookingId ||
      pendingBooking?.id ||
      pendingBooking?.booking?.booking_id ||
      pendingBooking?.booking?.bookingId ||
      pendingBooking?.booking?.id ||
      ""
  ).trim();
  const pendingDateKey = (() => {
    const directDateValue = String(pendingSlot?.date || "").trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(directDateValue)) {
      return directDateValue;
    }

    const parsedPendingDate = parseSlotDate(pendingStartTime);
    return parsedPendingDate ? formatDateKey(parsedPendingDate) : "";
  })();
  const pendingTimeLabel = String(
    pendingSlot?.time_label || pendingSlot?.timeLabel || ""
  ).trim();

  const matchingSlotIndex = baseSlots.findIndex((slot) => {
    const slotId = String(slot?.slot_id || slot?.id || "").trim();
    const startTime = String(slot?.start_time || slot?.startTime || "").trim();
    const slotStartDate = parseSlotDate(startTime);
    const slotDateKey = slotStartDate ? formatDateKey(slotStartDate) : "";
    const slotTimeLabel = slotStartDate ? formatSlotTimeRange(slot) : "";

    return (
      (pendingSlotId && slotId === pendingSlotId) ||
      (pendingStartTime && startTime === pendingStartTime) ||
      (pendingDateKey &&
        pendingTimeLabel &&
        slotDateKey === pendingDateKey &&
        slotTimeLabel === pendingTimeLabel)
    );
  });

  if (matchingSlotIndex !== -1) {
    const matchingSlot = baseSlots[matchingSlotIndex];
    const nextSlot = {
      ...matchingSlot,
      pending_booking_id:
        matchingSlot?.pending_booking_id || pendingBookingId,
      is_pending_payment: true,
    };

    return baseSlots.map((slot, index) =>
      index === matchingSlotIndex ? nextSlot : slot
    );
  }

  if (!pendingSlotId && !pendingStartTime) {
    return baseSlots;
  }

  return [
    ...baseSlots,
    {
      ...pendingSlot,
      id: pendingSlot?.id || pendingSlotId,
      slot_id: pendingSlot?.slot_id || pendingSlotId,
      start_time: pendingSlot?.start_time || pendingSlot?.startTime,
      end_time: pendingSlot?.end_time || pendingSlot?.endTime,
      pending_booking_id: pendingBookingId,
      is_pending_payment: true,
    },
  ];
};

const buildTutorModel = (teacher = {}, fallbackTutorId = "") => {
  if (!teacher || typeof teacher !== "object" || !Object.keys(teacher).length) {
    return null;
  }

  const rawPricePerSession = teacher?.price_per_session ?? teacher?.price;
  const pricePerSession = normalizeAudPriceForDisplay(rawPricePerSession);
  const yearIds = Array.isArray(teacher?.year)
    ? teacher.year
        .map((entry) => Number(entry?.id ?? entry))
        .filter((entry) => Number.isFinite(entry) && entry > 0)
    : Array.isArray(teacher?.yearIds)
      ? teacher.yearIds
          .map((entry) => Number(entry))
          .filter((entry) => Number.isFinite(entry) && entry > 0)
      : [];

  return {
    ...teacher,
    id: teacher?.id || fallbackTutorId || "",
    name:
      teacher?.name ||
      teacher?.full_name ||
      teacher?.teacher_name ||
      "Tutor not selected",
    email: getTeacherEmail(teacher),
    image: teacher?.photoImage || teacher?.photo || teacher?.image || "",
    price: pricePerSession ?? 0,
    priceMinorUnits: normalizeAudPriceToMinorUnits(rawPricePerSession),
    yearIds,
  };
};

const getUserIdentifier = (user = {}) =>
  user?.UserId || user?.user_id || user?.id || user?.pk || null;

const getUserDisplayName = (user = {}) =>
  [
    user?.name,
    user?.user_name,
    user?.full_name,
    user?.fullName,
    user?.first_name,
    user?.firstName,
  ]
    .map((value) => String(value || "").trim())
    .find(Boolean) || "Guest";

const getBookingIdFromPayload = (payload = {}) =>
  String(
    payload?.booking_id ||
      payload?.bookingId ||
      payload?.id ||
      payload?.booking?.booking_id ||
      payload?.booking?.bookingId ||
      payload?.booking?.id ||
      ""
  ).trim();

const getHeldBookingSlotId = (payload = {}) =>
  String(
    payload?.slot?.id ||
      payload?.slot?.slot_id ||
      payload?.slot_id ||
      payload?.booking?.slot?.id ||
      payload?.booking?.slot?.slot_id ||
      payload?.booking?.slot_id ||
      ""
  ).trim();

const getHeldBookingStartTime = (payload = {}) =>
  String(
    payload?.slot?.start_time ||
      payload?.slot?.startTime ||
      payload?.booking?.slot?.start_time ||
      payload?.booking?.slot?.startTime ||
      ""
  ).trim();

const getHeldBookingDateKey = (payload = {}) => {
  const directDateValue = String(
    payload?.slot?.date ||
      payload?.booking?.slot?.date ||
      ""
  ).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(directDateValue)) {
    return directDateValue;
  }

  const bookingStartTime = getHeldBookingStartTime(payload);
  const parsedBookingStartTime = parseSlotDate(bookingStartTime);
  return parsedBookingStartTime ? formatDateKey(parsedBookingStartTime) : "";
};

const getHeldBookingTimeLabel = (payload = {}) =>
  String(
    payload?.slot?.time_label ||
      payload?.slot?.timeLabel ||
      payload?.booking?.slot?.time_label ||
      payload?.booking?.slot?.timeLabel ||
      ""
  ).trim();

const getHeldBookingYearId = (payload = {}) =>
  Number(
    payload?.year?.id ||
      payload?.year_id ||
      payload?.booking?.year?.id ||
      payload?.booking?.year_id ||
      0
  );

const normalizeBookingStatusValue = (value) =>
  String(value || "").trim().toLowerCase();

const normalizePaymentAttemptStatusValue = (value) =>
  String(value || "").trim().toLowerCase();

const getBookingStatusFromPayload = (payload = {}) =>
  normalizeBookingStatusValue(payload?.status || payload?.booking?.status);

const getPaymentStatusFromPayload = (payload = {}) =>
  normalizeBookingStatusValue(
    payload?.payment_status || payload?.booking?.payment_status
  );

const getPaymentAttemptStatusFromPayload = (payload = {}) =>
  normalizePaymentAttemptStatusValue(
    payload?.paymentAttemptStatus ||
      payload?.payment_attempt_status ||
      payload?.booking?.paymentAttemptStatus ||
      payload?.booking?.payment_attempt_status
  );

const getPaidAtFromPayload = (payload = {}) =>
  payload?.paid_at || payload?.booking?.paid_at || "";

const isBookingPaidOrConfirmed = (payload = {}) =>
  getPaymentStatusFromPayload(payload) === "paid" ||
  getBookingStatusFromPayload(payload) === "confirmed";

const isSamePendingBookingSelection = ({
  booking,
  selectedSlotId,
  selectedStartTime,
  selectedDateKey,
  selectedTimeLabel,
  selectedYearId,
}) => {
  if (!booking || isBookingPaidOrConfirmed(booking)) {
    return false;
  }

  const bookingSlotId = getHeldBookingSlotId(booking);
  const bookingStartTime = getHeldBookingStartTime(booking);
  const bookingDateKey = getHeldBookingDateKey(booking);
  const bookingTimeLabel = getHeldBookingTimeLabel(booking);
  const bookingYearId = getHeldBookingYearId(booking);

  const matchesSlotIdentity =
    (selectedSlotId && bookingSlotId === String(selectedSlotId).trim()) ||
    (selectedStartTime &&
      bookingStartTime === String(selectedStartTime).trim());
  const matchesDate =
    !selectedDateKey ||
    !bookingDateKey ||
    bookingDateKey === String(selectedDateKey).trim();
  const matchesTimeLabel =
    selectedTimeLabel &&
    bookingTimeLabel === String(selectedTimeLabel).trim();
  const matchesSlot = matchesSlotIdentity || Boolean(matchesDate && matchesTimeLabel);
  const matchesYear =
    !selectedYearId ||
    !bookingYearId ||
    bookingYearId === Number(selectedYearId);

  return Boolean(matchesSlot && matchesYear && getBookingIdFromPayload(booking));
};

const normalizeHeldBookingPayload = (payload = {}) => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const bookingId = getBookingIdFromPayload(payload);
  const nestedBooking = payload?.booking && typeof payload.booking === "object"
    ? payload.booking
    : null;

  return {
    ...nestedBooking,
    ...payload,
    booking_id: bookingId || nestedBooking?.booking_id || "",
    bookingId: bookingId || nestedBooking?.bookingId || "",
    slot:
      payload?.slot ||
      nestedBooking?.slot ||
      (payload?.slot_id ? { id: payload.slot_id } : undefined),
    year:
      payload?.year ||
      nestedBooking?.year ||
      (payload?.year_id ? { id: payload.year_id } : undefined),
    status:
      payload?.status ||
      nestedBooking?.status ||
      "",
    payment_status:
      payload?.payment_status ||
      nestedBooking?.payment_status ||
      "",
    paid_at: getPaidAtFromPayload(payload),
  };
};

const buildBookingConfirmedPath = ({
  bookingId,
  tutorId,
  checkoutSessionId,
  bookingBasePath = "/classBooking",
}) =>
  `${bookingBasePath}/bookingConfirmed?bookingId=${encodeURIComponent(
    bookingId || ""
  )}&tutorId=${encodeURIComponent(tutorId || "")}&session_id=${encodeURIComponent(
    checkoutSessionId || ""
  )}&checkout_session_id=${encodeURIComponent(checkoutSessionId || "")}`;

const buildBookingPagePath = ({
  bookingId,
  tutorId,
  bookingBasePath = "/classBooking",
}) => {
  const params = new URLSearchParams();
  if (bookingId) {
    params.set("bookingId", bookingId);
  }
  if (tutorId) {
    params.set("tutorId", tutorId);
  }

  const search = params.toString();
  return search ? `${bookingBasePath}/booking?${search}` : `${bookingBasePath}/booking`;
};

const buildTutorDetailsPath = ({
  tutorId,
  bookingBasePath = "/classBooking",
}) => {
  const normalizedTutorId = String(tutorId || "").trim();
  if (!normalizedTutorId) {
    return `${bookingBasePath}/bookTutor`;
  }

  return `${bookingBasePath}/tutorDetails/${encodeURIComponent(normalizedTutorId)}`;
};

const normalizeTutorIdentifier = (value) => String(value || "").trim();

const getSummaryTutorIdentifiers = (summary = {}) =>
  Array.from(
    new Set(
      [
        summary?.tutorId,
        summary?.tutor_id,
        summary?.tutor?.id,
        summary?.tutor?.teacher_id,
        summary?.tutor?.teacherId,
        summary?.tutor?.uuid,
        summary?.heldBooking?.tutor_id,
        summary?.heldBooking?.tutor?.id,
        summary?.heldBooking?.tutor?.teacher_id,
        summary?.heldBooking?.tutor?.teacherId,
        summary?.heldBooking?.tutor?.uuid,
        summary?.booking?.tutor_id,
        summary?.booking?.tutor?.id,
        summary?.booking?.tutor?.teacher_id,
        summary?.booking?.tutor?.teacherId,
        summary?.booking?.tutor?.uuid,
      ]
        .map((value) => normalizeTutorIdentifier(value))
        .filter(Boolean)
    )
  );

const doesSummaryMatchTutor = (summary = {}, tutorId = "") => {
  const normalizedTutorId = normalizeTutorIdentifier(tutorId);
  if (!normalizedTutorId) {
    return true;
  }

  const summaryTutorIdentifiers = getSummaryTutorIdentifiers(summary);
  return (
    !summaryTutorIdentifiers.length ||
    summaryTutorIdentifiers.includes(normalizedTutorId)
  );
};

export const buildBookingContextFromSummary = (summary = {}) => {
  if (!summary || typeof summary !== "object") {
    return null;
  }

  const normalizedBookingPayload = normalizeHeldBookingPayload(
    summary?.heldBooking || summary
  );

  if (!normalizedBookingPayload) {
    return null;
  }

  return {
    ...normalizedBookingPayload,
    paymentAttemptStatus:
      summary?.heldBooking?.paymentAttemptStatus ||
      summary?.heldBooking?.payment_attempt_status ||
      summary?.paymentAttemptStatus ||
      summary?.payment_attempt_status ||
      "",
    payment_attempt_status:
      summary?.heldBooking?.payment_attempt_status ||
      summary?.heldBooking?.paymentAttemptStatus ||
      summary?.payment_attempt_status ||
      summary?.paymentAttemptStatus ||
      "",
  };
};

const getPendingTutorBookings = ({
  tutorId = "",
  activeSummary = null,
  pendingBookings = [],
}) => {
  const normalizedTutorId = normalizeTutorIdentifier(tutorId);
  const result = [];
  const bookingIdToIndex = new Map();

  const upsertPendingBooking = (summary, { preferSummaryContext = false } = {}) => {
    if (!summary || typeof summary !== "object") {
      return;
    }

    if (!doesSummaryMatchTutor(summary, normalizedTutorId)) {
      return;
    }

    const bookingPayload = preferSummaryContext
      ? buildBookingContextFromSummary(summary)
      : normalizeHeldBookingPayload(summary?.heldBooking || summary);
    const bookingId = getBookingIdFromPayload(bookingPayload);
    if (!bookingPayload || !bookingId) {
      return;
    }

    if (isBookingPaidOrConfirmed(bookingPayload) || isBookingPaidOrConfirmed(summary)) {
      return;
    }

    const nextEntry = {
      booking: bookingPayload,
      selectedSlot:
        summary?.selectedSlot ||
        summary?.slot ||
        summary?.booking?.slot ||
        null,
    };

    if (!bookingIdToIndex.has(bookingId)) {
      bookingIdToIndex.set(bookingId, result.length);
      result.push(nextEntry);
      return;
    }

    const existingIndex = bookingIdToIndex.get(bookingId);
    const existingEntry = result[existingIndex];
    const mergedBooking = {
      ...existingEntry.booking,
      ...nextEntry.booking,
      paymentAttemptStatus:
        nextEntry.booking?.paymentAttemptStatus ||
        existingEntry.booking?.paymentAttemptStatus ||
        "",
      payment_attempt_status:
        nextEntry.booking?.payment_attempt_status ||
        existingEntry.booking?.payment_attempt_status ||
        "",
    };

    result[existingIndex] = {
      booking: mergedBooking,
      selectedSlot: existingEntry.selectedSlot || nextEntry.selectedSlot,
    };
  };

  (Array.isArray(pendingBookings) ? pendingBookings : []).forEach((booking) => {
    upsertPendingBooking(booking);
  });
  upsertPendingBooking(activeSummary, { preferSummaryContext: true });

  return result;
};

export const resolveActiveBookingContext = ({
  heldBooking = null,
  selectedPendingBooking = null,
  storedPendingBookingContext = null,
  selectedSlotId = "",
  selectedSlotStartTime = "",
  selectedDateKey = "",
  selectedTimeLabel = "",
  selectedYearId = 0,
} = {}) => {
  const doesHeldBookingMatchSelection = isSamePendingBookingSelection({
    booking: heldBooking,
    selectedSlotId,
    selectedStartTime: selectedSlotStartTime,
    selectedDateKey,
    selectedTimeLabel,
    selectedYearId,
  });
  const doesStoredPendingBookingMatchSelection = isSamePendingBookingSelection({
    booking: storedPendingBookingContext,
    selectedSlotId,
    selectedStartTime: selectedSlotStartTime,
    selectedDateKey,
    selectedTimeLabel,
    selectedYearId,
  });

  return (
    (doesHeldBookingMatchSelection ? heldBooking : null) ||
    selectedPendingBooking ||
    (doesStoredPendingBookingMatchSelection ? storedPendingBookingContext : null)
  );
};

export const shouldClearCancelledPaymentState = ({
  bookingStatusMessage = "",
  selectedTimeId = "",
  activeBookingContext = null,
} = {}) =>
  bookingStatusMessage === PAYMENT_CANCELLED_MESSAGE &&
  Boolean(selectedTimeId) &&
  !activeBookingContext;

const BookingPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const bookingBasePath = getClassBookingBasePath(location.pathname);
  const isParentFlow = bookingBasePath.startsWith("/parent/");
  const bookingSource = getBookingSourceFromPath(location.pathname);
  const { currentYear } = useSelector((state) => state.profile);
  const userState = useSelector((state) => state.user);
  const user = userState?.user;
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const bookingIdFromQuery = String(searchParams.get("bookingId") || "").trim();
  const queryTutorId = String(searchParams.get("tutorId") || "").trim();
  const storedBookingSummary = useMemo(
    () => readStoredBookingSummary(bookingIdFromQuery),
    [bookingIdFromQuery]
  );
  const activeBookingId =
    bookingIdFromQuery ||
    String(
      storedBookingSummary?.bookingId ||
        storedBookingSummary?.heldBooking?.booking_id ||
        ""
    ).trim();
  const stateTutor = location.state?.tutor || null;
  const tutorId =
    location.state?.tutorId ||
    queryTutorId ||
    stateTutor?.id ||
    storedBookingSummary?.tutorId ||
    storedBookingSummary?.tutor?.id ||
    null;
  const isPaymentCancelled = useMemo(
    () => searchParams.get("payment") === "cancelled",
    [searchParams]
  );
  const [selectedDateKey, setSelectedDateKey] = useState(null);
  const [selectedTimeId, setSelectedTimeId] = useState(null);
  const [bookingStatus, setBookingStatus] = useState({ type: "", message: "" });
  const [heldBooking, setHeldBooking] = useState(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentUiState, setPaymentUiState] = useState(PAYMENT_UI_STATES.IDLE);
  const activeCheckoutBookingIdRef = useRef("");
  const tutorDetailQuery = useQuery({
    queryKey: ["class-booking-tutor-detail", tutorId],
    queryFn: () => teacherApi.getTutorDetail(tutorId),
    enabled: Boolean(tutorId),
    retry: false,
  });
  const bookingTutor = useMemo(() => {
    const stateTutorModel = buildTutorModel(stateTutor, tutorId);
    const storedTutorModel = buildTutorModel(storedBookingSummary?.tutor, tutorId);
    const detailTutorModel = buildTutorModel(tutorDetailQuery.data, tutorId);
    const firstTutorModel = stateTutorModel || storedTutorModel || detailTutorModel;

    if (!firstTutorModel) return null;

    return {
      ...(detailTutorModel || {}),
      ...(storedTutorModel || {}),
      ...(stateTutorModel || {}),
      email:
        stateTutorModel?.email ||
        storedTutorModel?.email ||
        detailTutorModel?.email ||
        "",
    };
  }, [stateTutor, storedBookingSummary?.tutor, tutorDetailQuery.data, tutorId]);

  const availabilityQuery = useQuery({
    queryKey: ["booking-tutor-availability", tutorId],
    queryFn: () => teacherApi.getTutorAvailability(tutorId),
    enabled: Boolean(tutorId),
    retry: false,
  });
  const bookingsQuery = useQuery({
    queryKey: ["student-class-booking-list"],
    queryFn: bookingService.getBookings,
    retry: false,
  });

  const normalizedApiAvailability = useMemo(
    () => normalizeAvailabilityCollection(availabilityQuery.data),
    [availabilityQuery.data]
  );

  const pendingTutorBookings = useMemo(
    () =>
      getPendingTutorBookings({
        tutorId,
        activeSummary: storedBookingSummary,
        pendingBookings: bookingsQuery.data?.pending || [],
      }),
    [bookingsQuery.data?.pending, storedBookingSummary, tutorId]
  );
  const pendingHeldBooking = useMemo(
    () =>
      normalizeHeldBookingPayload(
        heldBooking ||
          storedBookingSummary?.heldBooking ||
          null
      ),
    [heldBooking, storedBookingSummary]
  );
  const availabilitySlots = useMemo(
    () => {
      const mergedPendingSlots = pendingTutorBookings.reduce(
        (nextSlots, entry) =>
          mergePendingSlotIntoAvailability({
            availabilitySlots: nextSlots,
            pendingBooking: entry.booking,
            fallbackSelectedSlot: entry.selectedSlot,
          }),
        normalizedApiAvailability
      );

      return mergePendingSlotIntoAvailability({
        availabilitySlots: mergedPendingSlots,
        pendingBooking: pendingHeldBooking,
        fallbackSelectedSlot: storedBookingSummary?.selectedSlot || null,
      });
    },
    [
      normalizedApiAvailability,
      pendingHeldBooking,
      pendingTutorBookings,
      storedBookingSummary,
    ]
  );
  const availabilityModel = useMemo(
    () => buildAvailabilityModel(availabilitySlots),
    [availabilitySlots]
  );
  const [activeMonthIndex, setActiveMonthIndex] = useState(0);

  useEffect(() => {
    setActiveMonthIndex(0);
  }, [availabilityModel.months]);

  useEffect(() => {
    if (!availabilityModel.hasBookableSlots) {
      setSelectedDateKey(null);
      setSelectedTimeId(null);
      return;
    }

    const firstBookableDate = availabilityModel.dates.find(hasBookableDateSlots);
    const hasSelectedDate = availabilityModel.dates.some(
      (item) => item.dateKey === selectedDateKey && hasBookableDateSlots(item)
    );

    if (!hasSelectedDate && firstBookableDate) {
      setSelectedDateKey(firstBookableDate.dateKey);
      setSelectedTimeId(null);
    }
  }, [
    availabilityModel.dates,
    availabilityModel.hasBookableSlots,
    selectedDateKey,
  ]);

  const activeMonth = availabilityModel.months[activeMonthIndex] || null;
  const month = activeMonth?.label || formatMonthLabel(new Date());
  const activeMonthDates = useMemo(
    () =>
      availabilityModel.dates.filter(
        (item) => item.monthKey === activeMonth?.key
      ),
    [availabilityModel.dates, activeMonth]
  );
  const monthCalendarCells = useMemo(() => {
    if (!activeMonth?.date) {
      const fallback = new Date();
      const fallbackParts = getAustraliaDateParts(fallback);
      return getCalendarCells(fallbackParts.year, fallbackParts.month);
    }

    return getCalendarCells(activeMonth.year, activeMonth.month);
  }, [activeMonth]);

  const availableDateMap = useMemo(
    () =>
      activeMonthDates.reduce((map, item) => {
        map.set(item.dayNumber, item);
        return map;
      }, new Map()),
    [activeMonthDates]
  );

  const selectedDate = useMemo(
    () =>
      availabilityModel.dates.find((item) => item.dateKey === selectedDateKey) || null,
    [availabilityModel.dates, selectedDateKey]
  );
  const timeSlots = useMemo(() => selectedDate?.slots || [], [selectedDate]);
  const selectedTime = useMemo(
    () =>
      timeSlots.find(
        (slot) => slot.id === selectedTimeId && !slot.isDisabled
      ) || null,
    [timeSlots, selectedTimeId]
  );

  useEffect(() => {
    if (
      selectedTimeId &&
      !timeSlots.some(
        (slot) => slot.id === selectedTimeId && !slot.isDisabled
      )
    ) {
      setSelectedTimeId(null);
    }
  }, [selectedTimeId, timeSlots]);
  const isPaymentEnabled = Boolean(selectedDate && selectedTime && tutorId);
  const tutor = useMemo(
    () =>
      bookingTutor || {
        id: tutorId || "",
        name: "Tutor not selected",
        price: 0,
        image: "",
        yearIds: [],
      },
    [bookingTutor, tutorId]
  );
  const selectedYearId =
    Number(location.state?.yearId) ||
    Number(storedBookingSummary?.yearId) ||
    getCanonicalYearId(currentYear) ||
    tutor.yearIds?.[0] ||
    0;

  const formattedDate = selectedDate?.displayDate || "Not selected";
  const selectedTimeLabel = selectedTime?.label || "Not selected";
  const duration = selectedTime ? getSlotDurationLabel(selectedTime.raw) : "60 minutes";
  const selectedSlotId = selectedTime?.raw?.slot_id || selectedTime?.raw?.id || "";
  const selectedSlotStartTime = String(
    selectedTime?.raw?.start_time || selectedTime?.raw?.startTime || ""
  ).trim();
  const selectedPendingBookingId = String(
    selectedTime?.raw?.pending_booking_id || ""
  ).trim();
  const selectedPendingBooking = useMemo(
    () => {
      if (selectedPendingBookingId) {
        const directMatch = pendingTutorBookings.find(
          (entry) => getBookingIdFromPayload(entry?.booking) === selectedPendingBookingId
        );
        if (directMatch?.booking) {
          return directMatch.booking;
        }
      }

      return (
        pendingTutorBookings.find((entry) =>
          isSamePendingBookingSelection({
            booking: entry?.booking,
            selectedSlotId,
            selectedStartTime: selectedSlotStartTime,
            selectedDateKey,
            selectedTimeLabel,
            selectedYearId,
          })
        )?.booking || null
      );
    },
    [
      pendingTutorBookings,
      selectedPendingBookingId,
      selectedDateKey,
      selectedSlotId,
      selectedSlotStartTime,
      selectedTimeLabel,
      selectedYearId,
    ]
  );
  const storedPendingBookingContext = useMemo(
    () => buildBookingContextFromSummary(storedBookingSummary),
    [storedBookingSummary]
  );
  const activeBookingContext = resolveActiveBookingContext({
    heldBooking,
    selectedPendingBooking,
    storedPendingBookingContext,
    selectedSlotId,
    selectedSlotStartTime,
    selectedDateKey,
    selectedTimeLabel,
    selectedYearId,
  });
  const activeBookingPaymentStatus = getPaymentStatusFromPayload(activeBookingContext);
  const activeBookingStatus = getBookingStatusFromPayload(activeBookingContext);
  const activePaymentAttemptStatus =
    getPaymentAttemptStatusFromPayload(activeBookingContext);
  const isCurrentBookingPaidOrConfirmed = Boolean(
    isBookingPaidOrConfirmed(activeBookingContext)
  );
  const isCurrentBookingPending = Boolean(
    activeBookingContext &&
      isPendingBooking({
        ...activeBookingContext,
        paymentAttemptStatus:
          activePaymentAttemptStatus ||
          activeBookingContext?.paymentAttemptStatus ||
          activeBookingContext?.payment_attempt_status ||
          "",
      })
  );
  const shouldShowPendingPaymentState = Boolean(
    !isCurrentBookingPaidOrConfirmed && isCurrentBookingPending
  );
  const bookingPageBackPath = useMemo(
    () =>
      buildTutorDetailsPath({
        tutorId,
        bookingBasePath,
      }),
    [bookingBasePath, tutorId]
  );
  const holdBookingMutation = useMutation({
    mutationFn: () =>
      bookingService.holdBooking({
        tutor_id: tutorId,
        slot_id: selectedSlotId,
        year_id: selectedYearId,
        booking_source: bookingSource,
      }),
  });
  const checkoutSessionMutation = useMutation({
    mutationFn: ({ bookingId }) =>
      bookingService.createCheckoutSession({
        booking_id: bookingId,
        booking_source: bookingSource,
        frontend_base_url: window.location.origin,
        success_url: `${window.location.origin}${bookingBasePath}/bookingConfirmed?bookingId=${bookingId}&tutorId=${encodeURIComponent(
          tutorId || ""
        )}&session_id={CHECKOUT_SESSION_ID}&checkout_session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}${bookingBasePath}/booking?bookingId=${bookingId}&tutorId=${encodeURIComponent(
          tutorId || ""
        )}&payment=cancelled`,
      }),
  });

  useEffect(() => {
    if (heldBooking || !storedBookingSummary?.heldBooking) {
      return;
    }

    const normalizedStoredHold = normalizeHeldBookingPayload(
      storedBookingSummary.heldBooking
    );
    const storedHoldBookingId = getBookingIdFromPayload(normalizedStoredHold);
    if (
      normalizedStoredHold &&
      (!activeBookingId || storedHoldBookingId === activeBookingId)
    ) {
      setHeldBooking(normalizedStoredHold);
    }
  }, [activeBookingId, heldBooking, storedBookingSummary]);

  useEffect(() => {
    if (!storedBookingSummary?.heldBooking || !availabilityModel.hasBookableSlots) {
      return;
    }

    const storedHold = normalizeHeldBookingPayload(storedBookingSummary.heldBooking);
    const storedHoldBookingId = getBookingIdFromPayload(storedHold);
    if (activeBookingId && storedHoldBookingId && storedHoldBookingId !== activeBookingId) {
      return;
    }
    const storedSlotId = getHeldBookingSlotId(storedHold);
    const storedStartTime =
      storedHold?.slot?.start_time ||
      storedHold?.booking?.slot?.start_time ||
      "";

    const matchingDate = availabilityModel.dates.find((dateItem) =>
      dateItem.slots.some((slot) => {
        const candidateSlotId = String(
          slot?.raw?.slot_id || slot?.raw?.id || ""
        ).trim();
        const candidateStartTime = String(
          slot?.raw?.start_time || slot?.raw?.startTime || ""
        ).trim();

        return (
          (storedSlotId && candidateSlotId === storedSlotId) ||
          (storedStartTime && candidateStartTime === String(storedStartTime).trim())
        );
      })
    );

    if (!matchingDate) {
      return;
    }

    const matchingSlot = matchingDate.slots.find((slot) => {
      const candidateSlotId = String(
        slot?.raw?.slot_id || slot?.raw?.id || ""
      ).trim();
      const candidateStartTime = String(
        slot?.raw?.start_time || slot?.raw?.startTime || ""
      ).trim();

      return (
        (storedSlotId && candidateSlotId === storedSlotId) ||
        (storedStartTime && candidateStartTime === String(storedStartTime).trim())
      );
    });

    if (!matchingSlot || matchingSlot.isDisabled) {
      return;
    }

    const shouldRestoreStoredSelection =
      !selectedDateKey &&
      !selectedTimeId;

    if (shouldRestoreStoredSelection) {
      setSelectedDateKey(matchingDate.dateKey);
      setSelectedTimeId(matchingSlot.id);
    }
  }, [
    activeBookingId,
    availabilityModel.dates,
    availabilityModel.hasBookableSlots,
    selectedDateKey,
    selectedTimeId,
    storedBookingSummary,
  ]);

  useEffect(() => {
    if (isPaymentCancelled) {
      return;
    }

    const storedBookingReturnNotice = readStoredBookingReturnNotice();
    if (!storedBookingReturnNotice) {
      return;
    }

    const noticeTutorId = String(storedBookingReturnNotice?.tutorId || "").trim();
    const noticeBookingId = String(storedBookingReturnNotice?.bookingId || "").trim();
    const matchesTutor =
      !noticeTutorId || !tutorId || noticeTutorId === String(tutorId).trim();
    const matchesBooking =
      !noticeBookingId ||
      !activeBookingId ||
      noticeBookingId === String(activeBookingId).trim();

    if (!matchesTutor || !matchesBooking) {
      return;
    }

    setBookingStatus({
      type: storedBookingReturnNotice?.type || "error",
      message:
        storedBookingReturnNotice?.message ||
        PAYMENT_CANCELLED_MESSAGE,
    });
    setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
    clearStoredBookingReturnNotice();
  }, [activeBookingId, isPaymentCancelled, location.key, tutorId]);

  useEffect(() => {
    if (isPaymentCancelled && !isCurrentBookingPaidOrConfirmed) {
      const bookingIdToPersist =
        activeBookingId ||
        getBookingIdFromPayload(pendingHeldBooking) ||
        getBookingIdFromPayload(storedBookingSummary);

      if (bookingIdToPersist) {
        persistBookingSummary({
          ...storedBookingSummary,
          bookingId: bookingIdToPersist,
          heldBooking:
            pendingHeldBooking ||
            storedBookingSummary?.heldBooking ||
            activeBookingContext?.heldBooking ||
            activeBookingContext ||
            null,
          selectedSlot: selectedTime?.raw || storedBookingSummary?.selectedSlot || null,
          tutor: bookingTutor || storedBookingSummary?.tutor || tutor,
          tutorId: tutorId || storedBookingSummary?.tutorId || "",
          yearId: selectedYearId || storedBookingSummary?.yearId || "",
          formattedDate,
          selectedTime: selectedTimeLabel,
          duration,
          status:
            activeBookingStatus || storedBookingSummary?.status || "pending",
          payment_status:
            activeBookingPaymentStatus ||
            storedBookingSummary?.payment_status ||
            "pending",
          paid_at:
            getPaidAtFromPayload(activeBookingContext) ||
            storedBookingSummary?.paid_at ||
            "",
          paymentAttemptStatus: "failed_or_cancelled",
        });
      }

      persistBookingReturnNotice({
        bookingId: bookingIdToPersist,
        tutorId: tutorId || storedBookingSummary?.tutorId || "",
        type: "error",
        message: PAYMENT_CANCELLED_MESSAGE,
      });

      const stripeReturnGuard = readStoredBookingStripeReturnGuard();
      const guardTutorId = String(stripeReturnGuard?.tutorId || "").trim();
      const shouldRestorePreviousBookingEntry =
        Number(stripeReturnGuard?.steps || 0) >= 2 &&
        (!guardTutorId ||
          !tutorId ||
          guardTutorId === String(tutorId).trim());

      clearStoredBookingStripeReturnGuard();

      if (shouldRestorePreviousBookingEntry) {
        navigate(-2);
        return;
      }

      setBookingStatus({
        type: "error",
        message: PAYMENT_CANCELLED_MESSAGE,
      });
      setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
    }
  }, [
    activeBookingContext,
    activeBookingId,
    activeBookingPaymentStatus,
    activeBookingStatus,
    bookingTutor,
    duration,
    formattedDate,
    isCurrentBookingPaidOrConfirmed,
    isPaymentCancelled,
    pendingHeldBooking,
    selectedTime,
    selectedTimeLabel,
    selectedYearId,
    storedBookingSummary,
    tutor,
    tutorId,
    navigate,
  ]);

  useEffect(() => {
    if (!isPaymentCancelled || !isCurrentBookingPaidOrConfirmed) {
      return;
    }

    setBookingStatus({ type: "", message: "" });
    navigate(
      buildBookingPagePath({
        bookingId: bookingIdFromQuery,
        tutorId,
        bookingBasePath,
      }),
      { replace: true }
    );
  }, [
    bookingBasePath,
    bookingIdFromQuery,
    isCurrentBookingPaidOrConfirmed,
    isPaymentCancelled,
    navigate,
    tutorId,
  ]);

  useEffect(() => {
    if (
      !shouldClearCancelledPaymentState({
        bookingStatusMessage: bookingStatus.message,
        selectedTimeId,
        activeBookingContext,
      })
    ) {
      return;
    }

    clearStoredBookingReturnNotice();
    setBookingStatus({ type: "", message: "" });
    setPaymentUiState(PAYMENT_UI_STATES.IDLE);
    navigate(
      buildBookingPagePath({
        bookingId: "",
        tutorId,
        bookingBasePath,
      }),
      { replace: true }
    );
  }, [
    activeBookingContext,
    bookingBasePath,
    bookingStatus.message,
    navigate,
    selectedTimeId,
    tutorId,
  ]);

  useEffect(() => {
    setHeldBooking(null);
    setIsPaymentModalOpen(false);
    if (!isPaymentCancelled) {
      setPaymentUiState(PAYMENT_UI_STATES.IDLE);
    }
  }, [isPaymentCancelled, selectedDateKey, selectedTimeId, selectedYearId, tutorId]);

  const handleHoldBookingError = async (error) => {
    const status = Number(error?.status || 0);
    const message = getClassBookingActionErrorMessage(error, "hold-slot");

    if ([400, 404, 409].includes(status)) {
      setSelectedTimeId(null);
      setHeldBooking(null);
      await queryClient.invalidateQueries({
        queryKey: ["booking-tutor-availability", tutorId],
      });
    }

    if (status === 409 || status === 400) {
      setBookingStatus({
        type: "error",
        message,
      });
      setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
      return;
    }

    if (status === 401) {
      setBookingStatus({
        type: "error",
        message: "Student authentication is required. Please log in again.",
      });
      setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
      return;
    }

    setBookingStatus({ type: "error", message });
    setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
  };

  const handleCheckoutError = async (error, bookingId) => {
    const status = Number(error?.status || 0);
    const message = getClassBookingActionErrorMessage(error, "checkout-session");
    const conflictPayload =
      error?.raw?.data && typeof error.raw.data === "object"
        ? normalizeHeldBookingPayload(error.raw.data)
        : null;
    const bookingIdToPersist =
      bookingId ||
      getBookingIdFromPayload(conflictPayload) ||
      getBookingIdFromPayload(heldBooking) ||
      activeBookingId;

    activeCheckoutBookingIdRef.current = "";

    if (bookingIdToPersist) {
      persistBookingSummary({
        ...storedBookingSummary,
        bookingId: bookingIdToPersist,
        heldBooking:
          conflictPayload ||
          heldBooking ||
          storedBookingSummary?.heldBooking ||
          null,
        selectedSlot: selectedTime?.raw || storedBookingSummary?.selectedSlot || null,
        tutor: bookingTutor || storedBookingSummary?.tutor || tutor,
        tutorId: tutorId || storedBookingSummary?.tutorId || "",
        yearId: selectedYearId || storedBookingSummary?.yearId || "",
        formattedDate,
        selectedTime: selectedTimeLabel,
        duration,
        status:
          getBookingStatusFromPayload(conflictPayload) ||
          activeBookingStatus ||
          storedBookingSummary?.status ||
          "pending",
        payment_status:
          getPaymentStatusFromPayload(conflictPayload) ||
          activeBookingPaymentStatus ||
          storedBookingSummary?.payment_status ||
          "pending",
        paid_at:
          getPaidAtFromPayload(conflictPayload) ||
          getPaidAtFromPayload(heldBooking) ||
          storedBookingSummary?.paid_at ||
          "",
        paymentAttemptStatus: "failed_or_cancelled",
      });
    }

    if (status === 401) {
      setBookingStatus({
        type: "error",
        message: "Student authentication is required. Please log in again.",
      });
      setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
      return;
    }

    if (status === 404) {
      setBookingStatus({
        type: "error",
        message,
      });
      setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
      return;
    }

    if (
      status === 409 &&
      matchesTechnicalError(error, ["already been paid", "already paid"])
    ) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["student-class-booking-dashboard"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["student-class-booking-list"],
        }),
      ]);

      if (conflictPayload) {
        setHeldBooking(conflictPayload);
        persistBookingSummary({
          ...storedBookingSummary,
          bookingId: bookingId || getBookingIdFromPayload(conflictPayload),
          heldBooking: conflictPayload,
          tutor: bookingTutor || storedBookingSummary?.tutor || tutor,
          tutorId: tutorId || storedBookingSummary?.tutorId || "",
          yearId: selectedYearId || storedBookingSummary?.yearId || "",
          formattedDate,
          selectedTime: selectedTimeLabel,
          duration,
          status: conflictPayload.status || storedBookingSummary?.status || "confirmed",
          payment_status:
            conflictPayload.payment_status ||
            storedBookingSummary?.payment_status ||
            "paid",
          paid_at:
            conflictPayload.paid_at || storedBookingSummary?.paid_at || "",
        });
      }

      toast.info("This session has already been paid.");
      setBookingStatus({
        type: "success",
        message: "This session has already been paid.",
      });
      setIsPaymentModalOpen(false);
      setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_CONFIRMED);

      const conflictCheckoutSessionId = String(
        conflictPayload?.checkout_session_id ||
          conflictPayload?.session_id ||
          conflictPayload?.booking?.checkout_session_id ||
          ""
      ).trim();

      if (bookingId && conflictCheckoutSessionId) {
        navigate(
          buildBookingConfirmedPath({
            bookingId,
            tutorId,
            checkoutSessionId: conflictCheckoutSessionId,
            bookingBasePath,
          }),
          { replace: true }
        );
        return;
      }

      navigate(`${bookingBasePath}/dashboard`, { replace: true });
      return;
    }

    if (status === 409) {
      setSelectedTimeId(null);
      setHeldBooking(null);
      await queryClient.invalidateQueries({
        queryKey: ["booking-tutor-availability", tutorId],
      });
      setBookingStatus({
        type: "error",
        message: "This booking slot is no longer available. Please choose another time.",
      });
      setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
      return;
    }

    setBookingStatus({ type: "error", message });
    setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
  };

  const handleStartPaymentConfirmation = () => {
    if (
      !isPaymentEnabled ||
      holdBookingMutation.isPending ||
      checkoutSessionMutation.isPending
    ) {
      return;
    }

    if (isCurrentBookingPaidOrConfirmed) {
      toast.info("This session has already been paid.");
      return;
    }

    if (!selectedTime || selectedTime.isDisabled) {
      setBookingStatus({
        type: "error",
        message: "Please choose an upcoming available time to continue.",
      });
      return;
    }

    if (!selectedYearId) {
      setBookingStatus({
        type: "error",
        message: "Please select a valid student year before continuing.",
      });
      return;
    }

    setBookingStatus({ type: "", message: "" });
    if (isPaymentCancelled) {
      navigate(
        buildBookingPagePath({
          bookingId: bookingIdFromQuery,
          tutorId,
          bookingBasePath,
        }),
        { replace: true }
      );
    }
    setIsPaymentModalOpen(true);
  };

  const handleClosePaymentModal = () => {
    if (holdBookingMutation.isPending || checkoutSessionMutation.isPending) {
      return;
    }

    setIsPaymentModalOpen(false);
  };

  const handleContinueToPayment = async () => {
    if (
      !isPaymentEnabled ||
      holdBookingMutation.isPending ||
      checkoutSessionMutation.isPending
    ) {
      return;
    }

    logTutorConfirmPayClicked({
      email: user?.email || null,
      user_name: getUserDisplayName(user),
      user_id: getUserIdentifier(user),
      tutor_id: tutor?.id || tutorId,
      tutor_name: tutor?.name || "Tutor",
      tutor_email: tutor?.email || null,
      tutor_details: {
        price: typeof tutor?.price === "number" ? tutor.price : null,
        selected_date: formattedDate,
        selected_time: selectedTimeLabel,
        duration,
        year_id: selectedYearId,
        booking_source: bookingSource,
        booking_path: location.pathname,
      },
    });

    if (!selectedYearId) {
      setBookingStatus({
        type: "error",
        message: "Please select a valid student year before continuing.",
      });
      return;
    }

    if (!selectedTime || selectedTime.isDisabled) {
      setBookingStatus({
        type: "error",
        message: "Please choose an upcoming available time to continue.",
      });
      setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
      return;
    }

    setBookingStatus({ type: "", message: "" });
    let activeHeldBooking = null;
    setPaymentUiState(PAYMENT_UI_STATES.CREATING_CHECKOUT);

    if (
      getBookingIdFromPayload(heldBooking) &&
      isSamePendingBookingSelection({
        booking: heldBooking,
        selectedSlotId,
        selectedStartTime: selectedSlotStartTime,
        selectedDateKey,
        selectedTimeLabel,
        selectedYearId,
      })
    ) {
      activeHeldBooking = heldBooking;
    } else if (selectedPendingBooking) {
      activeHeldBooking = selectedPendingBooking;
      setHeldBooking(selectedPendingBooking);
    } else {
      try {
        const holdPayload = await holdBookingMutation.mutateAsync();
        activeHeldBooking = normalizeHeldBookingPayload(holdPayload);
        setHeldBooking(activeHeldBooking);
      } catch (error) {
        const holdErrorMessage = String(error?.message || "").toLowerCase();
        const reusablePendingBooking =
          selectedPendingBooking ||
          pendingTutorBookings.find((entry) =>
            isSamePendingBookingSelection({
              booking: entry?.booking,
              selectedSlotId,
              selectedStartTime: selectedSlotStartTime,
              selectedDateKey,
              selectedTimeLabel,
              selectedYearId,
            })
          )?.booking ||
          null;

        if (
          reusablePendingBooking &&
          (holdErrorMessage.includes("already reserved or booked") ||
            holdErrorMessage.includes("already reserved") ||
            holdErrorMessage.includes("already booked"))
        ) {
          activeHeldBooking = reusablePendingBooking;
          setHeldBooking(reusablePendingBooking);
          setBookingStatus({ type: "", message: "" });
        } else {
          await handleHoldBookingError(error);
          return;
        }
      }
    }

    const bookingId = getBookingIdFromPayload(activeHeldBooking);
    if (!bookingId) {
      setBookingStatus({
        type: "error",
        message: "Booking ID is missing. Please try again.",
      });
      setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
      return;
    }

    if (isBookingPaidOrConfirmed(activeHeldBooking)) {
      toast.info("This session has already been paid.");
      setBookingStatus({
        type: "success",
        message: "This session has already been paid.",
      });
      setIsPaymentModalOpen(false);
      setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_CONFIRMED);
      return;
    }

    if (activeCheckoutBookingIdRef.current === bookingId) {
      return;
    }

    activeCheckoutBookingIdRef.current = bookingId;

    try {
      const checkoutPayload = await checkoutSessionMutation.mutateAsync({
        bookingId,
      });
      const checkoutData = checkoutPayload || {};
      const checkoutUrl = checkoutData?.checkout_url || "";

      if (!checkoutUrl) {
        setBookingStatus({
          type: "error",
          message: "Stripe checkout URL is missing. Please try again.",
        });
        activeCheckoutBookingIdRef.current = "";
        setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
        return;
      }

      const bookingSummaryPayload = {
        tutor: {
          ...tutor,
          price:
            normalizeAudPriceForDisplay(checkoutData?.amount) ||
            Number(checkoutData?.booking?.session_fee) ||
            Number(activeHeldBooking?.tutor?.price_per_session ?? tutor.price) ||
            tutor.price,
        },
        formattedDate,
        selectedTime: selectedTimeLabel,
        duration,
        bookingId,
        heldBooking: activeHeldBooking,
        selectedSlot: selectedTime?.raw || storedBookingSummary?.selectedSlot || null,
        tutorId,
        yearId: selectedYearId,
        booking_source: bookingSource,
        status:
          checkoutData?.booking?.status ||
          activeHeldBooking?.status ||
          storedBookingSummary?.status ||
          "",
        payment_status:
          checkoutData?.payment_status ||
          checkoutData?.booking?.payment_status ||
          activeHeldBooking?.payment_status ||
          "pending",
        paid_at: getPaidAtFromPayload(checkoutData) || "",
        paymentAttemptStatus: "checkout_started",
        checkout_session_id:
          checkoutData?.checkout_session_id || storedBookingSummary?.checkout_session_id || "",
      };

      persistBookingSummary(bookingSummaryPayload);
      persistBookingStripeReturnGuard({
        bookingId,
        tutorId,
        steps: 2,
      });
      setIsPaymentModalOpen(false);
      setPaymentUiState(PAYMENT_UI_STATES.REDIRECTING);
      window.location.assign(checkoutUrl);
    } catch (error) {
      await handleCheckoutError(error, bookingId);
    }
  };

  const canTriggerPayment = Boolean(
    isPaymentEnabled &&
      !checkoutSessionMutation.isPending &&
      !holdBookingMutation.isPending &&
      !isCurrentBookingPaidOrConfirmed &&
      paymentUiState !== PAYMENT_UI_STATES.CREATING_CHECKOUT &&
      paymentUiState !== PAYMENT_UI_STATES.REDIRECTING
  );
  const summaryContinueLabel = isCurrentBookingPaidOrConfirmed
    ? "Paid"
    : paymentUiState === PAYMENT_UI_STATES.CREATING_CHECKOUT ||
        paymentUiState === PAYMENT_UI_STATES.REDIRECTING
      ? "Redirecting to secure checkout..."
      : isPaymentCancelled
        ? "Try Again"
        : "Continue to Payment";
  const summaryBadgeLabel = isCurrentBookingPaidOrConfirmed
    ? "Paid"
    : shouldShowPendingPaymentState
      ? "Payment Pending"
      : null;
  const summaryStatusDescription = isCurrentBookingPaidOrConfirmed
    ? "This booking has already been paid and confirmed."
    : shouldShowPendingPaymentState
      ? "Payment is still pending for this booking."
      : "";
  const summaryBookingStatusLabel = isCurrentBookingPaidOrConfirmed
    ? activeBookingStatus
    : shouldShowPendingPaymentState
      ? activeBookingStatus || "pending"
      : "";

  return (
    <PageContainer variant={isParentFlow ? "parent" : "default"}>
      <div
        className={`sticky top-0 z-50 ${
          isParentFlow ? "bg-[#f8f3ea]/95" : "bg-[#DFF2EB]/70"
        } backdrop-blur-md`}
      >
        <Header
          backLabel={isParentFlow ? "Back to Browse Tutors" : "Back to Profile"}
          backTo={isPaymentCancelled ? bookingPageBackPath : ""}
          parentLogoutTone={isParentFlow ? "violet" : "orange"}
        />
      </div>

      <MainContent
        className="pb-20 px-12"
        variant={isParentFlow ? "parent" : "default"}
      >
        <div className="space-y-6">
          <div>
            <h2
              className={`text-xl font-semibold ${
                isParentFlow ? "text-[#0B2B66]" : "text-gray-900"
              }`}
            >
              {isParentFlow ? "Choose a Tutor Booking Slot" : "Select Date & Time"}
            </h2>
            <p className="text-gray-600 mt-1">
              {isParentFlow
                ? `Choose an available slot for ${tutor.name} using the student's active plan access.`
                : `Choose an available slot with ${tutor.name}`}
            </p>
          </div>

          {!bookingTutor && tutorDetailQuery.isLoading ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-6 text-gray-600 shadow-sm">
              Loading tutor details...
            </div>
          ) : null}

          {!bookingTutor && !tutorDetailQuery.isLoading ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700 shadow-sm">
              Tutor information is missing. Go back to the tutor details page and choose
              a tutor again.
            </div>
          ) : null}

          {bookingStatus.message ? (
            <div
              className={`rounded-2xl p-5 shadow-sm ${
                bookingStatus.type === "error"
                  ? "border border-red-200 bg-red-50 text-red-700"
                  : "border border-green-200 bg-green-50 text-green-700"
              }`}
            >
              {bookingStatus.message}
            </div>
          ) : null}

          <div className="grid items-stretch gap-10 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
            <div className="flex h-full flex-col bg-white rounded-2xl p-8 shadow-md border border-gray-200">
              <div className="flex items-center gap-2 text-gray-800 font-medium">
                <Calendar size={20} />
                Select Date
              </div>

              <div className="flex justify-between items-center mt-6">
                <button
                  className={`p-2 rounded-full ${
                    activeMonthIndex > 0 ? "bg-gray-100" : "bg-gray-100/60 opacity-50"
                  }`}
                  type="button"
                  onClick={() => setActiveMonthIndex((current) => Math.max(0, current - 1))}
                  disabled={activeMonthIndex <= 0}
                >
                  <ChevronLeft size={18} className="text-gray-600" />
                </button>

                <h3 className="text-lg font-semibold">{month}</h3>

                <button
                  className={`p-2 rounded-full ${
                    activeMonthIndex < availabilityModel.months.length - 1
                      ? "bg-[#0736D6]"
                      : "bg-[#0736D6]/50 opacity-50"
                  }`}
                  type="button"
                  onClick={() =>
                    setActiveMonthIndex((current) =>
                      Math.min(availabilityModel.months.length - 1, current + 1)
                    )
                  }
                  disabled={activeMonthIndex >= availabilityModel.months.length - 1}
                >
                  <ChevronRight size={18} className="text-white" />
                </button>
              </div>

              <div className="grid grid-cols-7 text-center text-gray-500 font-medium mt-6">
                <span>Sun</span><span>Mon</span><span>Tue</span><span>Wed</span>
                <span>Thu</span><span>Fri</span><span>Sat</span>
              </div>

              <div className="grid grid-cols-7 text-center mt-4 gap-y-4">
                {monthCalendarCells.map((cell) => {
                  if (cell.type === "blank") {
                    return (
                      <div
                        key={cell.id}
                        aria-hidden="true"
                        className="w-10 h-10 mx-auto"
                      />
                    );
                  }

                  const day = cell.value;
                  const availableDate = availableDateMap.get(day) || null;
                  const available = Boolean(
                    availableDate && hasBookableDateSlots(availableDate)
                  );
                  const isPastOnlyDate = Boolean(availableDate && !available);
                  const selected = available && selectedDate?.dayNumber === day;
                  const dayButtonClass = selected
                    ? "bg-[#0736D6] text-white"
                    : available
                      ? "bg-gray-100 text-gray-800 hover:bg-gray-200"
                      : isPastOnlyDate
                        ? "bg-gray-100/70 text-gray-400"
                      : "text-gray-300";

                  return (
                    <button
                      key={day}
                      disabled={!available}
                      onClick={() => {
                        setSelectedDateKey(availableDate?.dateKey || null);
                        setSelectedTimeId(null);
                      }}
                      className={`
                        w-10 h-10 flex items-center justify-center rounded-full mx-auto transition
                        ${dayButtonClass}
                      `}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>

              <div className="mt-10 mb-3 flex items-center gap-2 text-gray-700 font-medium">
                <Clock size={18} /> Available Times
              </div>

              {availabilityQuery.isLoading ? (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                  Loading available slots...
                </div>
              ) : availabilityQuery.isError ? (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                  {getClassBookingQueryErrorMessage(
                    availabilityQuery.error,
                    "availability"
                  )}
                </div>
              ) : !availabilityModel.hasBookableSlots ? (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                  No available dates or slots for this tutor.
                </div>
              ) : (
                <div className="flex gap-4 flex-wrap">
                  {timeSlots.map((slot) => {
                    const isDisabled = !selectedDate || slot.isDisabled;

                    return (
                      <button
                        key={slot.id}
                        disabled={isDisabled}
                        onClick={() => {
                          if (isDisabled) return;
                          setSelectedTimeId(slot.id);
                        }}
                        className={`
                          px-6 py-2 rounded-xl border text-sm transition
                          ${
                            selectedTime?.id === slot.id
                              ? "border-[#0736D6] bg-[#EAEFFF] text-[#0736D6] font-semibold"
                              : isDisabled
                                ? "border-gray-200 bg-gray-100 text-gray-400"
                                : "border-gray-300 text-gray-700 bg-white hover:bg-gray-100"
                          }
                          ${isDisabled ? "cursor-not-allowed opacity-60 hover:bg-gray-100" : ""}
                        `}
                      >
                        {slot.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex">
              <BookingSummary 
                tutor={tutor}
                selectedTime={selectedTimeLabel}
                formattedDate={formattedDate}
                duration={duration}
                onContinue={handleStartPaymentConfirmation}
                isPaymentEnabled={canTriggerPayment}
                continueLabel={summaryContinueLabel}
                paymentBadgeLabel={summaryBadgeLabel}
                paymentBadgeTone={
                  isCurrentBookingPaidOrConfirmed ? "success" : "warning"
                }
                statusDescription={summaryStatusDescription}
                isPaymentDisabled={isCurrentBookingPaidOrConfirmed}
                bookingStatusLabel={summaryBookingStatusLabel}
                variant={isParentFlow ? "parent" : "default"}
              />
            </div>
          </div>
        </div>
      </MainContent>
      {isPaymentModalOpen ? (
        <PaymentModal
          tutor={tutor}
          formattedDate={formattedDate}
          selectedTime={selectedTimeLabel}
          duration={duration}
          onClose={handleClosePaymentModal}
          onConfirm={handleContinueToPayment}
          isSubmitting={
            holdBookingMutation.isPending ||
            checkoutSessionMutation.isPending ||
            paymentUiState === PAYMENT_UI_STATES.CREATING_CHECKOUT ||
            paymentUiState === PAYMENT_UI_STATES.REDIRECTING
          }
          confirmLabel={
            paymentUiState === PAYMENT_UI_STATES.CREATING_CHECKOUT ||
            paymentUiState === PAYMENT_UI_STATES.REDIRECTING
              ? "Redirecting to secure checkout..."
              : "Confirm & Pay"
          }
          variant={isParentFlow ? "parent" : "default"}
        />
      ) : null}
    </PageContainer>
  );
};

export default BookingPage;
